import React, { useEffect, useState } from "react";
import { api, fmtGNF, openAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Banknote, Receipt, Loader2, Download } from "lucide-react";
import { toast } from "sonner";

export default function CashierPage() {
  const [students, setStudents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [otherFees, setOtherFees] = useState([]);
  const [form, setForm] = useState({
    student_id: "", payment_plan_id: "", installment_id: "",
    amount_gnf: 0, method: "cash", payment_type: "tuition",
    reference: "", payer_name: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);
  const [otherForm, setOtherForm] = useState({ student_id: "", fee_type: "books", label: "", amount_gnf: 0, due_date: "", notes: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/scolarite/students-search");
      setStudents(data);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (form.student_id) {
      api.get("/scolarite/payment-plans", { params: { student_id: form.student_id } })
        .then(({ data }) => setPlans(data));
    } else setPlans([]);
  }, [form.student_id]);

  const currentPlan = plans.find(p => p.id === form.payment_plan_id);
  const pendingInstallments = currentPlan?.installments?.filter(i => i.status !== "paid") || [];

  const submit = async () => {
    if (!form.student_id || !form.amount_gnf) return toast.error("Élève + montant requis");
    setSaving(true);
    try {
      const { data } = await api.post("/scolarite/payments/record", {
        ...form,
        amount_gnf: parseFloat(form.amount_gnf),
        payment_plan_id: form.payment_plan_id || undefined,
        installment_id: form.installment_id || undefined,
      });
      toast.success(`Reçu ${data.receipt_number} émis`);
      setLastReceipt(data);
      setForm({ ...form, amount_gnf: 0, reference: "", notes: "", installment_id: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const submitOther = async () => {
    if (!otherForm.student_id || !otherForm.label || !otherForm.amount_gnf) return toast.error("Champs requis");
    try {
      await api.post("/scolarite/other-fees", { ...otherForm, amount_gnf: parseFloat(otherForm.amount_gnf) });
      toast.success("Frais annexe enregistré");
      setOtherForm({ student_id: "", fee_type: "books", label: "", amount_gnf: 0, due_date: "", notes: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const downloadReceipt = async (paymentId) => {
    try {
      await openAuthedFile(`/payments/${paymentId}/receipt`);
    } catch { toast.error("Impossible d'ouvrir le reçu PDF"); }
  };

  return (
    <div className="space-y-6" data-testid="cashier-page">
      <div>
        <div className="chip mb-2"><Banknote className="h-3 w-3" /><span>ENCAISSEMENT</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Caisse — Encaisser un paiement</h1>
        <p className="text-slate-500 mt-1">Espèces, Mobile Money, virement, chèque — un reçu officiel est généré à chaque paiement.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment form */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-3">
          <h2 className="font-heading font-bold text-[#1E3A8A]">Nouveau paiement</h2>
          <div>
            <Label>Élève *</Label>
            <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v, payment_plan_id: "", installment_id: "" })}>
              <SelectTrigger data-testid="c-student"><SelectValue placeholder="Rechercher..." /></SelectTrigger>
              <SelectContent>
                {students.map(s => <SelectItem key={s.id} value={s.id}>{s.matricule} — {s.first_name} {s.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {plans.length > 0 && (
            <div>
              <Label>Plan de paiement (optionnel)</Label>
              <Select value={form.payment_plan_id || "__none"} onValueChange={(v) => setForm({ ...form, payment_plan_id: v === "__none" ? "" : v, installment_id: "" })}>
                <SelectTrigger data-testid="c-plan"><SelectValue placeholder="Paiement libre" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Paiement libre (sans plan)</SelectItem>
                  {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.plan_type} — Solde {fmtGNF(p.balance_gnf)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {pendingInstallments.length > 0 && (
            <div>
              <Label>Échéance à créditer (optionnel)</Label>
              <Select value={form.installment_id || "__auto"} onValueChange={(v) => setForm({ ...form, installment_id: v === "__auto" ? "" : v })}>
                <SelectTrigger data-testid="c-inst"><SelectValue placeholder="Auto (imputation FIFO)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto">Auto (imputation FIFO)</SelectItem>
                  {pendingInstallments.map(i => <SelectItem key={i.id} value={i.id}>{i.label} — {fmtGNF(i.amount_gnf - (i.paid_amount || 0))} dû</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Montant (GNF) *</Label>
              <Input type="number" value={form.amount_gnf} onChange={e => setForm({ ...form, amount_gnf: e.target.value })} data-testid="c-amount" />
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="c-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Espèces</SelectItem>
                  <SelectItem value="orange_money">Orange Money</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="bank_transfer">Virement</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.payment_type} onValueChange={(v) => setForm({ ...form, payment_type: v })}>
                <SelectTrigger data-testid="c-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="registration">Inscription</SelectItem>
                  <SelectItem value="tuition">Scolarité</SelectItem>
                  <SelectItem value="books">Manuels</SelectItem>
                  <SelectItem value="canteen">Cantine</SelectItem>
                  <SelectItem value="transport">Transport</SelectItem>
                  <SelectItem value="uniform">Uniforme</SelectItem>
                  <SelectItem value="activities">Activités</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Référence (Mobile Money, chèque…)</Label>
              <Input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} data-testid="c-ref" />
            </div>
          </div>
          <div>
            <Label>Nom payeur</Label>
            <Input value={form.payer_name} onChange={e => setForm({ ...form, payer_name: e.target.value })} data-testid="c-payer" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="c-notes" />
          </div>
          <Button onClick={submit} disabled={saving} className="w-full bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="c-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Receipt className="h-4 w-4" />Enregistrer le paiement</>}
          </Button>
        </div>

        {/* Right column: last receipt + other fees */}
        <div className="space-y-4">
          {lastReceipt && (
            <div className="bg-green-50 border-2 border-green-300 rounded-2xl p-5" data-testid="last-receipt">
              <div className="font-heading font-bold text-green-900 mb-2">✅ Reçu émis</div>
              <div className="text-sm space-y-1">
                <div><span className="text-slate-500">N° :</span> <strong className="font-mono">{lastReceipt.receipt_number}</strong></div>
                <div><span className="text-slate-500">Élève :</span> {lastReceipt.student_name}</div>
                <div><span className="text-slate-500">Montant :</span> <strong>{fmtGNF(lastReceipt.amount_gnf)}</strong></div>
                <div><span className="text-slate-500">Mode :</span> {lastReceipt.method}</div>
              </div>
              <Button onClick={() => downloadReceipt(lastReceipt.id)} className="w-full mt-3 bg-[#1E3A8A] text-white gap-2" data-testid="dl-receipt">
                <Download className="h-4 w-4" />Télécharger reçu PDF
              </Button>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-3">
            <h2 className="font-heading font-bold text-[#1E3A8A]">Ajouter des frais annexes</h2>
            <div>
              <Label>Élève</Label>
              <Select value={otherForm.student_id} onValueChange={(v) => setOtherForm({ ...otherForm, student_id: v })}>
                <SelectTrigger data-testid="of-student"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={otherForm.fee_type} onValueChange={(v) => setOtherForm({ ...otherForm, fee_type: v })}>
                  <SelectTrigger data-testid="of-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="books">Manuels</SelectItem>
                    <SelectItem value="canteen">Cantine</SelectItem>
                    <SelectItem value="transport">Transport</SelectItem>
                    <SelectItem value="uniform">Uniforme</SelectItem>
                    <SelectItem value="activities">Activités</SelectItem>
                    <SelectItem value="exam">Examen</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Échéance</Label>
                <Input type="date" value={otherForm.due_date} onChange={e => setOtherForm({ ...otherForm, due_date: e.target.value })} data-testid="of-due" />
              </div>
            </div>
            <div>
              <Label>Libellé</Label>
              <Input value={otherForm.label} onChange={e => setOtherForm({ ...otherForm, label: e.target.value })} placeholder="Ex : Manuels 6ème" data-testid="of-label" />
            </div>
            <div>
              <Label>Montant (GNF)</Label>
              <Input type="number" value={otherForm.amount_gnf} onChange={e => setOtherForm({ ...otherForm, amount_gnf: e.target.value })} data-testid="of-amount" />
            </div>
            <Button onClick={submitOther} className="w-full bg-[#EA580C] text-white" data-testid="of-submit">Ajouter le frais</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
